import { createContext, useContext, useState } from "react";

import { useStore } from "react-redux";
import jwt_decode from "jwt-decode";
import { cacheClearPrefix } from "@redux/actions/cachedApiActions";

import useLogout from "@hooks/useLogout";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const CACHE_VERSION = "v1"; // bump on deploy to invalidate all cached keys

const getStoredAuthToken = () => localStorage.getItem("authToken") || "";

const getTokenPayload = (authToken) => {
  if (!authToken) return null;

  try {
    return jwt_decode(authToken);
  } catch {
    return null;
  }
};

const AuthProvider = ({ children }) => {
  // localStorage is synchronous and shared by same-origin tabs. Hydrate before
  // rendering children so route-level requests never send an empty token.
  const [payload, setPayload] = useState(() =>
    getTokenPayload(getStoredAuthToken())
  );
  const [token, setToken] = useState(getStoredAuthToken);
  const {
    logout: logoutUser,
    logoffAnnuitantToken,
    logoutGenericAnnuitant,
    logoutIncomeParticipant,
  } = useLogout();

  const store = useStore();

  const annuityToken = sessionStorage.getItem("annuityAuthToken") || "";
  const rkcToken = sessionStorage.getItem("rkcToken") || "";
  const path = window.location.pathname;
  const isViewGenericUserAccountSummary = path.includes(
    "/View/GenericUserAccount/Summary"
  );
  const isIncomeActivation = path.includes("/View/Account/IncomeActivation");

  const clearAllCaches = () =>
    store.dispatch(cacheClearPrefix(`${CACHE_VERSION}:`));

  const logout = () => {
    logoutUser();
    setPayload("");
    setToken("");
    clearAllCaches();
  };

  const annuityLogout = () => {
    isViewGenericUserAccountSummary
      ? logoutGenericAnnuitant()
      : isIncomeActivation
        ? logoutIncomeParticipant()
        : logoffAnnuitantToken();
    sessionStorage.removeItem("annuityAuthToken");
    sessionStorage.removeItem("rkcToken");
    sessionStorage.removeItem("activeTab");
  };

  const value = {
    user: payload,
    setPayload,
    token,
    setToken,
    logout,
    annuityToken,
    annuityLogout,
    rkcToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
